import type { QueryClient } from "@tanstack/react-query";
import {
  getAllUsersInOrgQuery,
  getAssessmentQuery,
  getCollectionQuery,
  getOrgDomainsQuery,
  getPackageAssessmentsQuery,
  getPackageQuery,
  listPublishedAssessmentsQuery,
  getQuestionsQuery,
  getUserProfileQuery,
  listCollectionsQuery,
  listPackagesQuery,
  userQuery,
  getPackageMetricsQuery,
  getPackageViewsQuery,
  listMyViewedTenderPackagesQuery,
  getPackageResultsQuery,
  listAllMyOrganisationsInfoQuery,
  listMyRequestsQuery,
  getAllWorkspaceJoinRequestsQuery,
  checkUserQuery,
  listMyNotificationsQuery,
  getMyInvitesQuery,
} from "@/queries.ts";
import { data, LoaderFunctionArgs, redirect } from "react-router";
import { lsConstants } from "./constants.ts";
import {
  availableOrgsQuery,
  orgInfoQuery,
  workspaceJoinRequestsQuery,
} from "./queries.ts";
import { OrganisationType, User, UserType } from "./api/user/types.ts";
import { unixSeconds } from "./service/date.ts";
import { isFeatureFlagOn } from "./service/featureFlags.ts";
import {
  getContractQuery,
  listContractMembersQuery,
  listContractsQuery,
  getSupplyChainQuery,
  getOrganisationSupplyChainQuery,
  markTenderPackageViewed,
  publicGetPackageQuery,
} from "./api/tenderPackage/index.ts";
import { TenderPackageStatus } from "./api/tenderPackage/types.ts";
import { listWorkspaceUsersQuery } from "./api/user/index.ts";
import { workspaceStore } from "./context/index.ts";
import { ExpiredError } from "./api/client.ts";
import {
  listAssessmentAnswersQuery,
  packageAssessmentResultsQuery,
  publicGetPackageCollectionsQuery,
  collectionVersionsQuery,
} from "./api/assessment/index.ts";
import { draftAssessmentUrl } from "./service/assessments/url.ts";

export const oauthLogin = async ({ request }: LoaderFunctionArgs) => {
  const { searchParams } = new URL(request.url);
  const expiresIn = searchParams.get("expires") || "0";
  const redirectPath = searchParams.get("redirect");
  const expirationDate = unixSeconds() + parseInt(expiresIn);
  localStorage.setItem(lsConstants.TOKEN_EXPIRES, String(expirationDate));

  if (redirectPath) localStorage.setItem(lsConstants.REDIRECT, redirectPath);

  throw redirect("/");
};

export const findWorkspace = (user: User, workspaceId: string) => {
  let organisation;
  let workspace;
  for (const org of user.organisations) {
    for (const space of org.workspaces || []) {
      if (space.id === workspaceId) {
        organisation = org;
        workspace = space;
        break;
      }
    }
  }
  return { organisation, workspace };
};

export const redirectToWorkspace = (client: QueryClient) => async () => {
  const user = await client.ensureQueryData(userQuery);

  if (!user) return;

  if (user && user.organisations.length === 0) throw redirect("/welcome");

  const sorted = user?.organisations.toSorted((a, b) =>
    a.name.localeCompare(b.name)
  );

  let firstAvailableWs = "";

  for (const org of sorted) {
    const saved = localStorage.getItem("workspaceId");

    // If we have their last workspace saved to localstorage,
    // Go straight to that workspace if user has permission.

    if (saved) {
      const { workspace } = findWorkspace(user, saved);

      if (workspace) throw redirect(`/w/${workspace.id}`);
    }

    // Otherwise, store first alphabetical workspace in the first alphabetical organisation
    if (!firstAvailableWs && org.workspaces?.length) {
      const sortedWs = org.workspaces.toSorted((a, b) =>
        a.name.localeCompare(b.name)
      );

      firstAvailableWs = sortedWs[0]?.id;
    }
  }

  // Redirect to the first workspace if there is one
  if (firstAvailableWs) throw redirect(`/w/${firstAvailableWs}`);

  // If I can't access any workspaces yet, go straight to onboarding flow
  throw redirect("/welcome/join/workspaces");
};

export const redirectToFirstOrganisation =
  (client: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const user = await client.ensureQueryData(userQuery);
    const orgs = user.organisations.toSorted((a, b) =>
      a.name.localeCompare(b.name)
    );

    const workspaceId = params.workspaceId;

    throw redirect(
      (workspaceId ? `/w/${workspaceId}` : "") + `/organisation/${orgs[0].id}`
    );
  };

export const getPublicTenderPackage =
  (client: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const packageId = safeParam(params, "packageId");
    const userPromise = client.ensureQueryData(checkUserQuery);
    const [, , user] = await Promise.all([
      client.ensureQueryData(publicGetPackageQuery(packageId)),
      client.ensureQueryData(publicGetPackageCollectionsQuery(packageId)),
      userPromise,
    ]);

    if (user) {
      // If logged in, redirect to the logged in version of this page.
      localStorage.setItem(
        lsConstants.REDIRECT,
        window.location.pathname + window.location.search
      );
      throw redirect("/");
    }
  };

export const getPrivateTenderPackage =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const packageId = safeParam(args.params, "packageId");
    const workspaceId = safeParam(args.params, "workspaceId");
    const { organisation } = await ensureWorkspace(client, args);

    client
      .ensureQueryData(publicGetPackageCollectionsQuery(packageId))
      .then((res) =>
        res.data.forEach((collection) =>
          collection.assessments.forEach((ass) =>
            client.ensureQueryData(listAssessmentAnswersQuery(ass.urlId))
          )
        )
      );

    client.ensureQueryData(packageAssessmentResultsQuery(packageId));
    client
      .ensureQueryData(publicGetPackageQuery(packageId))
      .then((tenderPackageInfo) => {
        const {
          organisationId: buyerOrganisationId,
          workspaceId: buyerWorkspaceId,
        } = tenderPackageInfo?.data?.[0] || {};
        const isViewedByOtherOrgs = buyerOrganisationId !== organisation.id;

        if (isViewedByOtherOrgs) {
          // No need to wait for this api before rendering the page.
          markTenderPackageViewed({
            organisationId: organisation.id,
            workspaceId,
            buyerOrganisationId,
            buyerWorkspaceId,
            tenderPackageId: packageId,
          });
        }
      });
  };

export const getUser =
  (client: QueryClient) =>
  async ({ request }: LoaderFunctionArgs) => {
    const { searchParams } = new URL(request.url);
    const redirectUrl = searchParams.get("redirect") || "";

    if (redirectUrl) localStorage.setItem(lsConstants.REDIRECT, redirectUrl);

    try {
      const user = await client.ensureQueryData(userQuery);
      return user;
    } catch {
      throw new ExpiredError("Session expired");
    }
  };

export const redirectToCorrectHome =
  (client: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const user = await client.ensureQueryData(userQuery);
    const workspaceId = safeParam(params, "workspaceId");
    const { organisation } = findWorkspace(user, workspaceId);
    const redirectTo = localStorage.getItem(lsConstants.REDIRECT);

    if (!organisation) throw redirect("/organisation");

    if (redirectTo && workspaceId) {
      localStorage.removeItem(lsConstants.REDIRECT);
      throw redirect(
        redirectTo.startsWith("/w/")
          ? redirectTo
          : `/w/${workspaceId}` + redirectTo
      );
    }

    const isBuyer = organisation.organisationType === OrganisationType.BUYER;
    if (isBuyer && isFeatureFlagOn("newDashboard")) return;
    if (isBuyer && isFeatureFlagOn("newPackages")) throw redirect("packages");
    if (isBuyer) throw redirect("tender-package");
    throw redirect("applications");
  };

export const checkAvailableOrgs =
  (client: QueryClient) =>
  async ({ request }: LoaderFunctionArgs) => {
    const available = await client.ensureQueryData(availableOrgsQuery);
    const invites = await client.ensureQueryData(getMyInvitesQuery);
    const { search } = new URL(request.url);
    const canJoin =
      available.organisations.length > 0 || invites.data.length > 0;
    throw redirect("/welcome/" + (canJoin ? "join" : "create") + search);
  };

export const getOrg =
  (client: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const organisationId = safeParam(params, "organisationId");
    const org = await client.ensureQueryData(orgInfoQuery(organisationId));

    if (!org) {
      throw data("Organisation not found", { status: 404 });
    }
    return org;
  };

const ensureWorkspace = async (
  client: QueryClient,
  { params }: LoaderFunctionArgs
) => {
  const user = await client.ensureQueryData(userQuery);
  const workspaceId = safeParam(params, "workspaceId");
  const { workspace, organisation } = findWorkspace(user, workspaceId);
  if (!workspace || !organisation) {
    throw data("You do not have permission to access this workspace", {
      status: 401,
    });
  }
  return { user, workspace, organisation };
};

const ensureOrganisation = async (
  client: QueryClient,
  { params }: LoaderFunctionArgs
) => {
  const user = await client.ensureQueryData(userQuery);
  const orgId = safeParam(params, "organisationId");
  const organisation = user.organisations.find((org) => org.id === orgId);
  if (!organisation) {
    throw data("You do not have permission to access this workspace", {
      status: 401,
    });
  }
  return { user, organisation };
};

export const getWorkspace =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const data = await ensureWorkspace(client, args);
    workspaceStore.set({
      workspace: data.workspace,
      organisation: data.organisation,
    });
    localStorage.setItem(lsConstants.WORKSPACE_ID, data.workspace.id);
  };

const safeParam = (params: Record<string, string | undefined>, key: string) => {
  const param = params[key];
  if (!param) {
    throw new Error(`Param :${key} not in route path`);
  }
  return param;
};

export const listCollections =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const { organisation, workspace } = await ensureWorkspace(client, args);
    const query = listCollectionsQuery(organisation.id, workspace.id);
    await client.ensureQueryData(query);
  };

export const listCollectionVersions =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const { workspace, organisation } = await ensureWorkspace(client, args);
    const collectionId = safeParam(args.params, "collectionId");
    const [versions] = await Promise.all([
      client.ensureQueryData(collectionVersionsQuery(collectionId)),
      client.ensureQueryData(getAllUsersInOrgQuery(organisation.id)),
    ]);

    const hasLiveOrArchivedCollection = versions.data.some(
      (version) => version.status === "LIVE" || version.status === "ARCHIVED"
    );

    if (!hasLiveOrArchivedCollection) {
      const collection = versions.data[0];
      return redirect(
        draftAssessmentUrl({
          workspaceId: workspace.id,
          collectionId,
          collectionType: collection.collectionType,
          assessmentId: collection.assessments?.[0].id,
        })
      );
    }
  };

export const getCollection =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    await ensureWorkspace(client, args);
    const collectionId = safeParam(args.params, "collectionId");
    const query = getCollectionQuery(collectionId);
    const { assessments, collectionType } = await client.ensureQueryData(query);
    const assessmentId = args.params.assessmentId;
    // Redirect to questions if this is a standalone collection
    // and we aren't already viewing a specific assessment

    if (
      collectionType !== "TWO_STAGE" &&
      assessments?.length === 1 &&
      !assessmentId
    ) {
      const id = assessments[0].id;
      return redirect(`a/${id}/detail`);
    }
  };

const findAssessment = async (
  client: QueryClient,
  args: LoaderFunctionArgs
) => {
  const collectionId = safeParam(args.params, "collectionId");
  const assessmentId = safeParam(args.params, "assessmentId");
  const assQuery = getAssessmentQuery({
    collectionId,
    assessmentId,
  });
  return client.ensureQueryData(assQuery);
};

export const getAssessment =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const { organisation, workspace } = await ensureWorkspace(client, args);
    const questionsQuery = getQuestionsQuery(organisation.id, workspace.id);
    // Don't wait for this to finish as it's not critical for the page
    client.ensureQueryData(questionsQuery);
    await findAssessment(client, args);
  };

// TODO: delete this once the new workspace flow is complete
export const getOrganisationDomains =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const { organisation } = await ensureOrganisation(client, args);
    await client.ensureQueryData(getOrgDomainsQuery(organisation.id));
  };

export const getOrganisationSettings =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const { organisation } = await ensureOrganisation(client, args);

    await Promise.all([
      client.ensureQueryData(orgInfoQuery(organisation.id)),
      client.ensureQueryData(workspaceJoinRequestsQuery(organisation.id)),
    ]);
  };

export const getAllOrgUsers =
  (client: QueryClient, includeInvites: boolean = false) =>
  async (args: LoaderFunctionArgs) => {
    const { organisation } = await ensureOrganisation(client, args);

    await client.ensureQueryData(
      getAllUsersInOrgQuery(organisation.id, includeInvites)
    );
  };

export const getUserProfile =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const { organisation } = await ensureWorkspace(client, args);
    const userId = safeParam(args.params, "userId");

    await client.ensureQueryData(getUserProfileQuery(organisation.id, userId));
  };

export const listContracts =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    await ensureWorkspace(client, args);
    await client.ensureQueryData(listContractsQuery());
  };

export const getSupplyChain =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    await ensureWorkspace(client, args);
    const view = new URL(args.request.url).searchParams.get("view");
    // The page falls back to the workspace view for non-admins, so the
    // organisation query is only warmed, never required
    await Promise.all([
      client.ensureQueryData(getSupplyChainQuery()),
      view === "organisation"
        ? client.prefetchQuery(getOrganisationSupplyChainQuery())
        : undefined,
    ]);
  };

export const listPackages =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    await ensureWorkspace(client, args);
    const query = listPackagesQuery();
    await client.ensureQueryData(query);
  };

export const getPackage =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    await ensureWorkspace(client, args);
    const id = safeParam(args.params, "id");
    // preload assessments as we'll need on a later page
    const published = listPublishedAssessmentsQuery();
    client.ensureQueryData(published);
    const ass = getPackageAssessmentsQuery(id);
    client.ensureQueryData(ass);
    const query = getPackageQuery(id);
    await client.ensureQueryData(query);
  };

export const getContract =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    await ensureWorkspace(client, args);
    const contractId = safeParam(args.params, "contractId");
    try {
      await client.ensureQueryData(getContractQuery(contractId));
    } catch {
      throw data("Contract not found", { status: 404 });
    }
  };

export const getContractMembers =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const { organisation, workspace } = await ensureWorkspace(client, args);
    const contractId = safeParam(args.params, "contractId");
    await Promise.all([
      client.ensureQueryData(listContractMembersQuery(contractId)),
      client.ensureQueryData(
        listWorkspaceUsersQuery(organisation.id, workspace.id, false)
      ),
    ]);
  };

export const getContractAssessments =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const contractId = safeParam(args.params, "contractId");
    await ensureWorkspace(client, args);
    const contract = await client.ensureQueryData(getContractQuery(contractId));
    if (!contract.data.startDate || !contract.data.endDate) {
      throw data("No start or end date set for this contract", 400);
    }
    if (contract.data.tenderPackageId) {
      client.ensureQueryData(
        getPackageAssessmentsQuery(contract.data.tenderPackageId)
      );
    }
  };

export const getPackageDetails =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    await ensureWorkspace(client, args);
    const id = safeParam(args.params, "id");
    const assQuery = getPackageAssessmentsQuery(id);
    client.ensureQueryData(assQuery);

    const query = getPackageQuery(id);
    const pkg = await client.ensureQueryData(query);

    const userAllowedToViewResults =
      pkg.status === TenderPackageStatus.REVIEW ||
      pkg.status === TenderPackageStatus.AWARDED;
    if (userAllowedToViewResults) {
      const resultsQuery = getPackageResultsQuery(id);
      client.ensureQueryData(resultsQuery);
    }
  };

export const getPackageMetrics =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    await ensureWorkspace(client, args);
    const id = safeParam(args.params, "id");
    const metricsQuery = getPackageMetricsQuery(id);
    client.ensureQueryData(metricsQuery);
    const viewsQuery = getPackageViewsQuery(id);
    client.ensureQueryData(viewsQuery);
  };

export const listMySubmissions =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const { organisation, workspace } = await ensureWorkspace(client, args);

    await client.ensureQueryData(
      listMyViewedTenderPackagesQuery({
        organisationId: organisation.id,
        workspaceId: workspace.id,
      })
    );
  };

export const ensureCanManageFeatures =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const user = await client.ensureQueryData(userQuery);
    const orgId = safeParam(args.params, "organisationId");

    const organisation = user.organisations.find((org) => org.id === orgId);

    const isOrgAdmin =
      organisation?.userType === UserType.OWNER ||
      organisation?.userType === UserType.ADMIN;

    if (!isOrgAdmin) {
      throw redirect("..");
    }
  };

export const ensureOrgInfo =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    await ensureWorkspace(client, args);
    const orgId = safeParam(args.params, "organisationId");
    client.ensureQueryData(availableOrgsQuery);
    client.ensureQueryData(orgInfoQuery(orgId));
    client.ensureQueryData(workspaceJoinRequestsQuery(orgId));
  };

export const listAllMyOrganisationsInfo = (client: QueryClient) => async () => {
  const allOrgInfo = client.ensureQueryData(listAllMyOrganisationsInfoQuery);
  const allRequests = client.ensureQueryData(listMyRequestsQuery);

  await Promise.all([allOrgInfo, allRequests]);
};

export const getOrgDomains =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const organisation = await getOrg(client)(args);
    await client.ensureQueryData(getOrgDomainsQuery(organisation.id));
  };

export const getAllWorkspaceJoinRequests =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const orgId = safeParam(args.params, "organisationId");

    await client.ensureQueryData(getAllWorkspaceJoinRequestsQuery(orgId));
  };

export const listAllMyNotifications =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    const { organisation, workspace } = await ensureWorkspace(client, args);

    await client.ensureQueryData(
      listMyNotificationsQuery(organisation.id, workspace.id)
    );
  };

export const getAssessmentWithSection =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    await ensureWorkspace(client, args);
    const collectionId = safeParam(args.params, "collectionId");
    const assessmentId = safeParam(args.params, "assessmentId");
    const { data: assessment } = await client.ensureQueryData(
      getAssessmentQuery({
        collectionId,
        assessmentId,
      })
    );

    const sectionId = safeParam(args.params, "sectionId");

    const section = assessment.sections?.find(
      (section) => section.id === sectionId
    );

    if (!section) throw redirect("../introduction");
  };

export const getAssessmentWithQuestion =
  (client: QueryClient) => async (args: LoaderFunctionArgs) => {
    await ensureWorkspace(client, args);
    const collectionId = safeParam(args.params, "collectionId");
    const assessmentId = safeParam(args.params, "assessmentId");
    const { data: assessment } = await client.ensureQueryData(
      getAssessmentQuery({
        collectionId,
        assessmentId,
      })
    );

    const sectionId = safeParam(args.params, "sectionId");
    const questionId = safeParam(args.params, "questionId");

    const section = assessment.sections?.find(
      (section) => section.id === sectionId
    );

    if (!section) throw redirect("../introduction");

    const question = section.questions.find((q) => q.id === questionId);

    if (!question) throw redirect("../introduction");
  };
